<script lang="ts">
  import ActorFollower from '#activities/components/actor_follower.svelte'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { normalizeError } from '#app/lib/error'
  import { onChange } from '#app/lib/svelte/svelte'
  import { treq } from '#app/lib/treq'
  import InfiniteScroll from '#components/infinite_scroll.svelte'
  import type { OrderedCollectionPage } from '#server/types/activity'
  import { i18n } from '#user/lib/i18n'

  let {
    actorName,
    actorLabel = $bindable(),
    standalone = false,
    followersCount = $bindable(0),
  }: {
    actorName: string
    actorLabel: string
    standalone?: boolean
    followersCount?: number
  } = $props()

  let flash: FlashState | undefined = $state()
  let fetching = $state(false)
  let hasMore = true
  let remoteFollowers = $state([])
  const limit = 10
  let offset = 0

  async function fetchMore () {
    try {
      fetching = true
      const res = await treq('activitypub', 'get', 'followers', {
        limit,
        offset,
        name: actorName,
        'with-actors-info': true,
      })
      const { orderedItems: remoteFollowersBatch } = res as OrderedCollectionPage
      const newUsers = remoteFollowersBatch.filter(newUser => !remoteFollowers.includes(newUser))
      if (newUsers.length === 0) hasMore = false
      remoteFollowers = [ ...remoteFollowers, ...newUsers ]
      fetching = false
      offset += limit
    } catch (err) {
      flash = normalizeError(err)
    }
  }
  async function keepScrolling () {
    if (fetching || hasMore === false) return false
    fetching = true
    try {
      await fetchMore()
      return true
    } catch (err) {
      flash = normalizeError(err)
      return false
    } finally {
      fetching = false
    }
  }

  fetchMore()

  $effect(() => {
    onChange(remoteFollowers, () => {
      followersCount = remoteFollowers.length
    })
  })
</script>

{#if standalone}
  <h2>{i18n('Fediverse followers of %{actorName}', { actorName })}</h2>
{/if}

<Flash state={flash} />

<InfiniteScroll {keepScrolling} showSpinner={true}>
  <ul>
    {#each remoteFollowers as follower (follower.id)}
      <ActorFollower {follower} />
    {/each}
  </ul>
</InfiniteScroll>

<style lang="scss">
  @import "general/scss/utils";
  h2{
    @include display-flex(column, stretch, center);
    margin: 0.5em 0;
    text-align: center;
  }
  ul{
    margin: auto;
    @include display-flex(row, center, flex-start, wrap);
    :global(li){
      flex: 1;
      min-width: min(25em, 90vw);
    }
  }
</style>
